import { Injectable, computed, inject, signal } from '@angular/core';
import { Observable, forkJoin, of, map, shareReplay, tap, catchError } from 'rxjs';
import { ProjectService } from '@data-access/services/project.service';
import { isVisibleProjectSource } from '@core/models/project-catalog.model';
import { StorageService } from './storage.service';
import { AuthService } from './auth.service';
import { NO_PROJECT_ID, Project, ProjectLocationFilter } from '@core/models/project.model';

const CURRENT_PROJECT_KEY = 'qms_current_project_id';
const LOCATION_FILTER_KEY = 'qms_location_filter';
const CURRENT_TAG_FILTER_KEY = 'qms_current_tag_filter';

/**
 * Holds the project the user is currently working inside (Group H).
 * Mirrors ADAP's current-project selection: the chosen project id is sent on every
 * API request via the X-Project-Id header (see project.interceptor.ts) and persisted
 * in localStorage so the selection survives reloads.
 */
@Injectable({ providedIn: 'root' })
export class ProjectContextService {
  private readonly projectService = inject(ProjectService);
  private readonly storage = inject(StorageService);
  private readonly auth = inject(AuthService);

  private readonly _currentProject = signal<Project | null>(null);
  private readonly _myProjects = signal<Project[]>([]);
  private readonly _loading = signal(false);
  private readonly _loaded = signal(false);
  private readonly _locationFilter = signal<ProjectLocationFilter>(null);
  /** The distinct tag strings across all projects (e.g. ["GOMBU", "SIVAP"]). */
  private readonly _availableTags = signal<string[]>([]);
  /** The tag string currently selected for filtering (e.g. "SIVAP"), or null when none. */
  private readonly _currentTagFilter = signal<string | null>(null);
  /** The current user's role in the selected project: 'Owner' | 'Member' | 'Viewer' | 'Admin' | ''. */
  private readonly _currentProjectRole = signal<string>('');
  private load$?: Observable<Project[]>;

  readonly currentProject = this._currentProject.asReadonly();
  readonly myProjects = this._myProjects.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly loaded = this._loaded.asReadonly();

  readonly availableTags = this._availableTags.asReadonly();
  readonly currentTagFilter = this._currentTagFilter.asReadonly();

  readonly isAdmin = this.auth.isAdmin;

  /** The current user's role within the selected project. */
  readonly currentProjectRole = this._currentProjectRole.asReadonly();

  /** True when the user can only view data in the current project (no actions allowed). */
  readonly isViewer = computed(() =>
    !this.isAdmin() && this._currentProjectRole().toLowerCase() === 'viewer');

  /** True when the user may create/edit/delete data in the current project. */
  readonly canEditData = computed(() => {
    if (this.isAdmin()) return true;
    const role = this._currentProjectRole().toLowerCase();
    return role === 'owner' || role === 'member' || role === 'admin';
  });

  /** True when the user may manage project membership (add owners/members/viewers). */
  readonly canManageMembers = computed(() => {
    if (this.isAdmin()) return true;
    return this._currentProjectRole().toLowerCase() === 'owner';
  });

  /** A project is selected, or an admin-level filter is active. */
  readonly hasSelection = computed(() =>
    this._currentProject() !== null ||
    this._currentTagFilter() !== null ||
    this._locationFilter() !== null);

  /** The id to attach to the X-Project-Id header (null = none / admin all-projects view). */
  readonly currentProjectId = computed(() => this._currentProject()?.id ?? null);

  /**
   * Admin onshore/offshore leadership roll-up filter (feedback ID 39). Only meaningful in the
   * all-projects view (no specific project selected); attached as the X-Project-Location header.
   */
  readonly locationFilter = this._locationFilter.asReadonly();

  /**
   * Load (once) the projects the current user can enter and restore/auto-select a current
   * project. Subsequent calls return the cached result. Used by the route guard and selector.
   */
  ensureLoaded(): Observable<Project[]> {
    if (this._loaded()) {
      return of(this._myProjects());
    }
    if (!this.load$) {
      this._loading.set(true);
      // Projects and tags are independent lookups — fetch them in parallel instead of
      // waiting for /projects/mine to resolve before issuing the tags request.
      this.load$ = forkJoin({
        projects: this.projectService.getMine().pipe(
          // EPPM projects are synchronized but withheld from users for now, so they are excluded
          // here too — this list drives the header project switcher and the route guard.
          map(projects => projects.filter(p => isVisibleProjectSource(p.source))),
          // A transient /projects/mine failure must not propagate as an unhandled error to
          // guards and field subscribers; degrade gracefully to an empty project list.
          catchError(() => of([] as Project[])),
        ),
        tags: this.projectService.getTags().pipe(
          catchError(() => of([] as string[])),
        ),
      }).pipe(
        tap({
          next: ({ projects, tags }) => {
            this._myProjects.set(projects);
            this._availableTags.set(tags);
            this.restoreSelection(projects);
            this._loading.set(false);
            this._loaded.set(true);
          },
          error: () => {
            this._loading.set(false);
            this._loaded.set(true);
            this.load$ = undefined;
          },
        }),
        map(({ projects }) => projects),
        catchError(() => of([] as Project[])),
        shareReplay(1),
      );
    }
    return this.load$;
  }

  /** Force a refresh of the project list from the API (e.g. after creating one). */
  reload(): Observable<Project[]> {
    this._loaded.set(false);
    this.load$ = undefined;
    return this.ensureLoaded();
  }

  setCurrentProject(project: Project | null): void {
    this._currentProject.set(project);
    // Selecting a specific project supersedes any tag/location filter.
    this._currentTagFilter.set(null);
    this.storage.remove(CURRENT_TAG_FILTER_KEY);
    if (project) {
      this.storage.set(CURRENT_PROJECT_KEY, project.id);
      this.setLocationFilter(null);
      this.loadRole(project.id);
    } else {
      this.storage.remove(CURRENT_PROJECT_KEY);
      this._currentProjectRole.set(this.isAdmin() ? 'Admin' : '');
    }
  }

  /** Fetch distinct tag strings across all projects (for the "View all" dropdown). */
  private loadTags(): void {
    this.projectService.getTags().subscribe({
      next: tags => this._availableTags.set(tags),
      error: () => this._availableTags.set([]),
    });
  }

  /** Refresh the list of available tags from the API (e.g. after a project's tags changed). */
  reloadTags(): void {
    this.loadTags();
  }

  /**
   * Select a tag to filter to every project that carries it (e.g. "SIVAP").
   * Supersedes any single-project selection / location roll-up. Mutually exclusive.
   */
  setCurrentTagFilter(tag: string | null): void {
    this._currentTagFilter.set(tag);
    if (tag) {
      this.storage.set(CURRENT_TAG_FILTER_KEY, tag);
      this._currentProject.set(null);
      this.storage.remove(CURRENT_PROJECT_KEY);
      this._locationFilter.set(null);
      this.storage.remove(LOCATION_FILTER_KEY);
      this._currentProjectRole.set(this.isAdmin() ? 'Admin' : '');
    } else {
      this.storage.remove(CURRENT_TAG_FILTER_KEY);
    }
  }

  /** Resolve and cache the current user's role for a project. Admins always have full access. */
  private loadRole(projectId: string): void {
    if (this.isAdmin()) {
      this._currentProjectRole.set('Admin');
      return;
    }
    this._currentProjectRole.set('');
    this.projectService.getMyRole(projectId).subscribe({
      next: role => this._currentProjectRole.set(role || 'Member'),
      error: () => this._currentProjectRole.set('Member'),
    });
  }

  /** Set the admin onshore/offshore roll-up filter; clears any specific project selection. */
  setLocationFilter(location: ProjectLocationFilter): void {
    this._locationFilter.set(location);
    if (location) {
      this.storage.set(LOCATION_FILTER_KEY, location);
      if (this._currentProject() !== null) {
        this._currentProject.set(null);
        this.storage.remove(CURRENT_PROJECT_KEY);
      }
      if (this._currentTagFilter() !== null) {
        this._currentTagFilter.set(null);
        this.storage.remove(CURRENT_TAG_FILTER_KEY);
      }
    } else {
      this.storage.remove(LOCATION_FILTER_KEY);
    }
  }

  /** Update an existing project in the list (e.g. after editing) and refresh the selection reference. */
  upsertProject(project: Project): void {
    this._myProjects.update(list => {
      const exists = list.some(p => p.id === project.id);
      const next = exists ? list.map(p => (p.id === project.id ? project : p)) : [...list, project];
      return next.sort((a, b) => a.name.localeCompare(b.name));
    });
    if (this._currentProject()?.id === project.id) {
      this._currentProject.set(project);
    }
  }

  /** Add a freshly created project to the list and select it. */
  addAndSelect(project: Project): void {
    this._myProjects.update(list => [...list, project].sort((a, b) => a.name.localeCompare(b.name)));
    this.setCurrentProject(project);
  }

  clearSelection(): void {
    this.setCurrentProject(null);
  }

  private restoreSelection(projects: Project[]): void {
    if (!this.isAdmin() && projects.length === 1 && projects[0].id === NO_PROJECT_ID) {
      this.setCurrentProject(projects[0]);
      return;
    }

    const current = this._currentProject();
    if (current && projects.some(p => p.id === current.id)) {
      this._currentProject.set(projects.find(p => p.id === current.id) ?? current);
      this.loadRole(current.id);
      return;
    }

    const storedId = this.storage.get<string>(CURRENT_PROJECT_KEY);
    const stored = storedId ? projects.find(p => p.id === storedId) : undefined;
    if (stored) {
      this._currentProject.set(stored);
      this.loadRole(stored.id);
      return;
    }

    // Restore a tag filter.
    const storedTag = this.storage.get<string>(CURRENT_TAG_FILTER_KEY);
    if (storedTag) {
      this._currentTagFilter.set(storedTag);
      this._currentProjectRole.set(this.isAdmin() ? 'Admin' : '');
      return;
    }

    // Restore an admin location roll-up filter (only valid in the all-projects view).
    if (this.isAdmin()) {
      const storedLocation = this.storage.get<ProjectLocationFilter>(LOCATION_FILTER_KEY);
      if (storedLocation === 'Onshore' || storedLocation === 'Offshore') {
        this._locationFilter.set(storedLocation);
        return;
      }
    }

    // Auto-select when there is exactly one project to enter (non-admin convenience).
    if (!this.isAdmin() && projects.length === 1) {
      this.setCurrentProject(projects[0]);
    }
  }
}
